import { ToggleButton } from '@simplywallst/ui-core';
import BoldTextSolid from '@simplywallst/ui-core/icons/BoldTextSolid';
import BulletListSolid from '@simplywallst/ui-core/icons/BulletListSolid';
import ImageUploadOutline from '@simplywallst/ui-core/icons/ImageUploadOutline';
import LinkOutline from '@simplywallst/ui-core/icons/LinkOutline';
import { useRichTextEditorContext } from '../hooks/useRichTextEditorContext';
import { Heading } from './Heading';
import { selectFile } from '../hooks/useFileUpload';
import { isDisabled } from '../utils';
import Carousel from '@/components/Carousel';
import useResizeObserver from 'use-resize-observer';
import { useState } from 'react';
import { useMedia } from '@/hooks/useMedia';
import { StyledEmbla } from './styled';
import { useTracking } from 'react-tracking';

const ICON_WIDTH = 48;
const ICON_GAP = 4;

export const ToolbarActions = () => {
  const { isActive, format, handleFileUpload, enableImages, editor } =
    useRichTextEditorContext();
  const isTouchMobile = useMedia(
    '(max-width: 600px) and (pointer: coarse)',
    false
  );
  const { ref: containerRef, width } = useResizeObserver();
  const [submenuItems, setSubmenuItems] = useState(0);
  const { trackEvent } = useTracking({
    type: 'track',
    subject: 'editor_toolbar',
  });
  const containerWidth = width || 100;

  const primarySlides = enableImages ? 5 : 4;
  const totalNumberOfSlides = primarySlides + submenuItems;
  const hasMoreNarratives =
    containerWidth < ICON_WIDTH * totalNumberOfSlides + ICON_GAP;

  return (
    <div className="grid w-full grid-cols-1" ref={containerRef}>
      <StyledEmbla>
        <Carousel
          align="center"
          containScroll="trimSnaps"
          showArrows={hasMoreNarratives && !isTouchMobile}
          showGradient={hasMoreNarratives}
          totalSlides={totalNumberOfSlides}
        >
          <div
            role="group"
            className="grid min-h-x5 auto-cols-max grid-flow-col items-center gap-x0_5"
          >
            <Heading
              format={format}
              isActive={isActive}
              onOpen={setSubmenuItems}
              onClose={() => setSubmenuItems(0)}
            />
            <ToggleButton
              styleType="subtle"
              onClick={() => format('bold')}
              disabled={isDisabled(
                ['heading1', 'heading2', 'heading3', 'image'],
                isActive
              )}
              state={isActive('bold') ? 'on' : 'off'}
              aria-pressed={isActive('bold')}
              aria-label="Toggle bold"
            >
              <BoldTextSolid />
            </ToggleButton>
            <ToggleButton
              styleType="subtle"
              onClick={() => format('bulletList')}
              disabled={isDisabled(
                ['heading1', 'heading2', 'heading3', 'image'],
                isActive
              )}
              state={isActive('bulletList') ? 'on' : 'off'}
              aria-pressed={isActive('bulletList')}
              aria-label="Toggle bullet list"
            >
              <BulletListSolid />
            </ToggleButton>
            {enableImages && (
              <ToggleButton
                styleType="subtle"
                onClick={() => {
                  trackEvent({ action: 'click', modifier: 'image' });
                  selectFile().then(handleFileUpload);
                }}
                disabled={isDisabled(
                  [
                    'heading1',
                    'heading2',
                    'heading3',
                    'bold',
                    'bulletList',
                    'orderedList',
                    'link',
                  ],
                  isActive
                )}
                state={isActive('image') ? 'on' : 'off'}
                aria-pressed={isActive('image')}
                aria-label="Add image"
              >
                <ImageUploadOutline />
              </ToggleButton>
            )}
            <ToggleButton
              styleType="subtle"
              onClick={() => {
                trackEvent({ action: 'click', modifier: 'link' });
                editor?.commands.toggleLink({ href: '' });
              }}
              disabled={
                (!isActive('link') &&
                  editor?.state.selection.from ===
                    editor?.state.selection.to) ||
                isDisabled(['image'], isActive)
              }
              state={isActive('link') ? 'on' : 'off'}
              aria-pressed={isActive('link')}
              aria-label="Toggle link"
              className="!overflow-hidden !px-x1"
            >
              <LinkOutline className="!h-x4 !w-x4" />
            </ToggleButton>
          </div>
        </Carousel>
      </StyledEmbla>
    </div>
  );
};
